import { useEffect, useId, useRef, useState, type UIEvent } from 'react';
import { Link } from 'react-router';

import { LINE_HEIGHT_PX, scrollTopFor, visibleRange } from '../code-window';
import { linesLabel } from '../ri-text';

interface CodeViewerProps {
  /** What the code is, for the region's name: a path, or a symbol. */
  label: string;
  lines: readonly string[];
  /** The line number of `lines[0]`. */
  firstLine: number;
  highlight?: { start: number; end: number } | null;
  /** Scrolled into view when the viewer opens or it changes. */
  focusLine?: number | null;
  /** A link for each line number — the line anchor. Without it, line numbers are plain text. */
  lineHref?: (line: number) => string;
}

/**
 * Code as plain text with line numbers (MASTER-SPEC §14 P14-05). Nothing in it is ever interpreted: each line is a
 * text node, so markup in a file is shown, never run. Long files render only the lines around what is on screen
 * (§14.10); short ones render whole, so a screen reader and the browser's find see all of it. Keyboard: the region
 * takes focus and scrolls with the arrow keys; line anchors are links.
 */
export function CodeViewer({
  label,
  lines,
  firstLine,
  highlight,
  focusLine,
  lineHref,
}: CodeViewerProps) {
  const id = useId();
  const container = useRef<HTMLDivElement>(null);
  const [viewport, setViewport] = useState({ top: 0, height: 800 });
  const range = visibleRange(lines.length, viewport.top, viewport.height);

  useEffect(() => {
    const element = container.current;
    if (!element || !focusLine) {
      return;
    }
    const index = focusLine - firstLine;
    element.scrollTop = scrollTopFor(index);
    // A short file is not windowed: the line exists, and the browser can bring it into view itself.
    element.querySelector(`[data-line="${focusLine}"]`)?.scrollIntoView?.({ block: 'center' });
  }, [focusLine, firstLine]);

  const onScroll = (event: UIEvent<HTMLDivElement>) => {
    const { scrollTop, clientHeight } = event.currentTarget;
    setViewport({ top: scrollTop, height: clientHeight || 800 });
  };

  const highlighted = (line: number) =>
    highlight ? line >= highlight.start && line <= highlight.end : false;
  const rows = [];
  for (let index = range.from; index < range.to; index += 1) {
    const line = firstLine + index;
    rows.push(
      <div
        key={line}
        data-line={line}
        className={`ri-code__line${highlighted(line) ? ' ri-code__line--highlighted' : ''}`}
      >
        {lineHref ? (
          <Link className="ri-code__number" to={lineHref(line)} replace aria-label={`Line ${line}`}>
            {line}
          </Link>
        ) : (
          <span className="ri-code__number" aria-hidden="true">
            {line}
          </span>
        )}
        <span className="ri-code__text">{lines[index]}</span>
      </div>,
    );
  }

  return (
    <>
      {highlight ? (
        <p id={`${id}-highlight`} className="sr-only">
          Highlighted: {linesLabel(highlight.start, highlight.end)}.
        </p>
      ) : null}
      <div
        ref={container}
        className="ri-code"
        role="region"
        aria-label={`Code: ${label}`}
        aria-describedby={highlight ? `${id}-highlight` : undefined}
        // A scrollable region has to take focus to be scrolled from the keyboard (WCAG 2.1.1).
        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
        tabIndex={0}
        onScroll={onScroll}
      >
        <div style={{ height: range.from * LINE_HEIGHT_PX }} aria-hidden="true" />
        {rows}
        <div style={{ height: (lines.length - range.to) * LINE_HEIGHT_PX }} aria-hidden="true" />
      </div>
    </>
  );
}
